import { useEffect, useState, useRef } from "react";
import useSWR from "swr";
import axios from "axios";


const fetchCategoryDetails = async (slug) => {
  const response = await axios.post(global.postUrl + "category/details", {
    category: slug,
  });
  if (response.data && response.data.success === true) {
    return response.data.result;
  }
  return {};
};

const useListing = (props) => {
  const hitsPerPage = 24;
  const categorySlug =
    props.match.params.id1 === "nose-pins"
      ? "nosepins"
      : props.match.params.id1;
  const { data: category = {}, error: categoryError } = useSWR(
    categorySlug ? `category-${categorySlug}` : null,
    () => fetchCategoryDetails(categorySlug),
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
      revalidateIfStale: false,
      dedupingInterval: Infinity,
    },
  );

  // Restore cached data on back navigation to avoid shimmer/loading flash
  const cachedData = useRef(null);
  if (cachedData.current === null) {
    cachedData.current = false;
    if (props.history.action === "POP") {
      try {
        const raw = sessionStorage.getItem("listing_cache");
        if (raw) {
          const data = JSON.parse(raw);
          if (data.pathname === props.location.pathname) {
            cachedData.current = data;
          }
        }
      } catch {}
    }
  }
  const cache = cachedData.current;
  const restoredFromCache = useRef(!!cache);

  const [fetchProducts, setFetchProducts] = useState(false);
  const [shimmer, setShimmer] = useState(cache ? false : true);
  const [productShimmer, setProductShimmer] = useState(cache ? false : true);
  const [showModal, setShowModal] = useState(false);
  const [loadItems, setLoadItems] = useState(false);
  const [loadRange, setLoadRange] = useState(false);

  const [searchQuery, setSearchQuery] = useState("");
  const [facetQuery, setFacetQuery] = useState([]);
  const [rangeQuery, setRangeQuery] = useState([]);
  const [searchResults, setSearchResults] = useState(
    cache ? cache.searchResults : [],
  );
  const [totalProducts, setTotalProducts] = useState(
    cache ? cache.totalProducts : 0,
  );
  const [totalPages, setTotalPages] = useState(cache ? cache.totalPages : 0);

  const [selectedPage, setSelectedPage] = useState(0);
  const [selectedFilters, setSelectedFilters] = useState({});
  const [selectedRangeFilters, setSelectedRangeFilters] = useState({});
  const aToZSort =
    window.filterOption.algolia_sorting.find(
      (option) => option.name === "Name: A to Z",
    ) || window.filterOption.algolia_sorting[0];
  const defaultSortValue = window.rc.algoliasearch.defaultIndex;
  const [selectedSorting, setSelectedSorting] = useState(defaultSortValue);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [lastPathname, setLastPathname] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [isFetching, setIsFetching] = useState(false);
  const loadingNextPage = useRef(false);
  const isLoadingFromUrl = useRef(false);
  // Monotonic id for search requests — used to ignore stale (superseded)
  // responses so an in-flight search can never overwrite fresher results.
  const searchSeq = useRef(0);
  // Snapshot of the filter-type deps from the previous run of the search
  // effect, used to tell a filter/sort/search change apart from a page change.
  const prevFilterDeps = useRef(null);

  const [facetFetchValues, setFacetFetchValues] = useState({});
  const [facetValues, setFacetValues] = useState(
    cache ? cache.facetValues : {},
  );
  const [facetStatsValues, setFacetStatsValues] = useState(
    cache ? cache.facetStatsValues : {},
  );

  const refresh = () => {
    setSelectedFilters({});
    setSelectedRangeFilters({});
    setSearchQuery("");
    setFacetQuery([]);
    setRangeQuery([]);
    setFacetStatsValues({});
    setLoadRange(false);
  };

  useEffect(() => {
    document.body.classList.add("listing-page");

    // On back navigation with cached data, scroll to the last viewed product
    if (cache) {
      try {
        const productId = sessionStorage.getItem("listing_scroll_product");
        if (productId) {
          requestAnimationFrame(() => {
            const el = document.getElementById(`product-${productId}`);
            if (el) {
              el.scrollIntoView({ block: "center" });
            }
          });
          sessionStorage.removeItem("listing_scroll_product");
        }
      } catch {}
    }

    return () => {
      document.body.classList.remove("listing-page");
    };
  }, []);

  // Detect mobile device on mount and resize
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    // Mark that we're loading from URL to prevent pagination reset
    isLoadingFromUrl.current = true;

    // Detect actual route change (not just query param change)
    const isRouteChange =
      lastPathname && lastPathname !== props.location.pathname;

    // On initial load or actual route change, refresh filters
    if (isInitialLoad || isRouteChange) {
      refresh();
    }
    setLastPathname(props.location.pathname);
    setIsInitialLoad(false);

    // Only scroll to top on forward navigation, not on back button or pagination
    if ((isInitialLoad || isRouteChange) && props.history.action !== "POP") {
      const element = document.getElementsByClassName("site-main");
      if (element.length > 0) {
        element[0].scrollTop = 0;
      }
      window.scrollTo(0, 0);
    }

    const queryParams = new URLSearchParams(window.location.search);
    const urlFilterList = queryParams.keys();

    let appliedFilters = [];
    let appliedQuery = "";
    let appliedRange = [];
    let selectedFiltersFromUrl = {};
    let rangeFromUrl = null;
    let pageFromUrl = 0; // Default to page 0 (first page)

    for (let option of urlFilterList) {
      if (option == "query") appliedQuery = queryParams.get(option);
      else if (option == "range") {
        const rangeStr = queryParams.get(option);
        // Parse min/max for UI display and Algolia query
        const parts = rangeStr.split("TO");
        if (parts.length === 2) {
          const min = Number(parts[0]);
          const max = Number(parts[1]);
          appliedRange.push(`variants.offerprice:${min} TO ${max}`);
          rangeFromUrl = { value: { min, max } };
        }
      } else if (option == "page") {
        // Read page number from URL (convert to 0-indexed)
        const pageNum = parseInt(queryParams.get(option), 10);
        pageFromUrl = pageNum > 0 ? pageNum - 1 : 0;
      } else if (window.filterOption.facetList.includes(option)) {
        const filterValues = queryParams.get(option).split(",");
        selectedFiltersFromUrl[option] = filterValues;
        // Build Algolia facet filter format
        const formattedFilter = filterValues.map((v) => `${option}:${v}`);
        appliedFilters.push(formattedFilter);
      }
    }
    if (window.location.pathname == "/trending")
      appliedFilters.push([`trending:true`]);
    else if (props.match.params.id1)
      appliedQuery =
        props.match.params.id1 == "nose-pins"
          ? "nosepins"
          : props.match.params.id1;

    // Set page from URL
    setSelectedPage(pageFromUrl);

    // Set all filter states from URL
    setSearchQuery(appliedQuery);
    setFacetQuery((prev) => {
      const prevStr = JSON.stringify(prev);
      const nextStr = JSON.stringify(appliedFilters);
      return prevStr === nextStr ? prev : appliedFilters;
    });
    setRangeQuery((prev) => {
      const prevStr = JSON.stringify(prev);
      const nextStr = JSON.stringify(appliedRange);
      return prevStr === nextStr ? prev : appliedRange;
    });

    if (Object.keys(selectedFiltersFromUrl).length > 0) {
      setSelectedFilters(selectedFiltersFromUrl);
    }

    if (rangeFromUrl) {
      setSelectedRangeFilters(rangeFromUrl);
    }

    if (selectedSorting == "") setSelectedSorting(defaultSortValue);
    setFetchProducts(true);

    // Reset the flag after state updates complete
    setTimeout(() => {
      isLoadingFromUrl.current = false;
    }, 100);
  }, [props.location.pathname, props.location.search]);

  const scrollListingToTop = () => {
    const element = document.getElementsByClassName("site-main");
    if (element.length > 0) {
      if (typeof element[0].scrollTo === "function") {
        element[0].scrollTo({ top: 0, behavior: "smooth" });
      } else {
        element[0].scrollTop = 0;
      }
    }

    window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
  };

  // On mobile, when loading a URL with page > 1, load all previous pages
  useEffect(() => {
    if (isMobile && selectedPage > 0 && fetchProducts) {
      // Load pages 0 to selectedPage-1 sequentially
      const loadPreviousPages = async () => {
        for (let i = 0; i < selectedPage; i++) {
          // This will trigger the search function which appends results on mobile
          await new Promise((resolve) => setTimeout(resolve, 300));
        }
      };
      loadPreviousPages();
    }
  }, [isMobile, selectedPage, fetchProducts]);

  const handlePageChange = (newPage) => {
    // Update URL with new page number (convert to 1-indexed for URL)
    const queryParams = new URLSearchParams(window.location.search);

    // Preserve existing filters in URL
    Object.keys(selectedFilters).forEach((facetName) => {
      if (selectedFilters[facetName] && selectedFilters[facetName].length > 0) {
        queryParams.set(facetName, selectedFilters[facetName].join(","));
      }
    });

    // Preserve range filter if present
    if (selectedRangeFilters && selectedRangeFilters.value) {
      queryParams.set(
        "range",
        `${selectedRangeFilters.value.min}TO${selectedRangeFilters.value.max}`,
      );
    }

    // Preserve search query if present
    // Don't add query param if it matches the category route param (id1)
    let isCategoryParam = false;
    if (props.match && props.match.params && props.match.params.id1) {
      const categoryParam =
        props.match.params.id1 === "nose-pins"
          ? "nosepins"
          : props.match.params.id1;
      if (searchQuery === categoryParam) {
        isCategoryParam = true;
      }
    }

    if (searchQuery && !isCategoryParam) {
      queryParams.set("query", searchQuery);
    }

    // Update page number (convert to 1-indexed for URL)
    if (newPage === 0) {
      queryParams.delete("page"); // Remove page param if it's page 1
    } else {
      queryParams.set("page", newPage + 1); // Store 1-indexed in URL
    }

    const newUrl = `${window.location.pathname}?${queryParams.toString()}`;
    props.history.push(newUrl);
    if (!isMobile) {
      requestAnimationFrame(scrollListingToTop);
    }
  };

  const loadNextPage = () => {
    // For mobile infinite scroll - load next page
    // Use ref guard to prevent double-trigger from observer race condition
    if (selectedPage < totalPages - 1 && !loadingNextPage.current) {
      loadingNextPage.current = true;
      const nextPage = selectedPage + 1;
      handlePageChange(nextPage);
    }
  };

  const isEmptySort =
    selectedSorting && selectedSorting.replace("products_list", "");

  const fetchFullPriceStats = () => {
    axios
      .post(global.postUrl + "restproxy", {
        headers: {
          "X-Algolia-Application-Id": window.rc.algoliasearch.appId,
          "X-Algolia-API-Key": window.rc.algoliasearch.apiKey,
        },
        body: {
          query: searchQuery,
          hitsPerPage: 0,
          facets: ["variants.offerprice"],
          facetFilters: facetQuery,
          page: 0,
        },
        url:
          "https://" +
          window.rc.algoliasearch.appId +
          "-dsn.algolia.net/1/indexes/" +
          !isEmptySort
            ? aToZSort.value
            : selectedSorting + "/query",
        force: false,
      })
      .then((response) => {
        const { facets_stats } = response.data.response;
        if (facets_stats) setFacetStatsValues(facets_stats);
      })
      .catch(() => {});
  };

  const search = () => {
    if (isFetching) return;
    setIsFetching(true);

    // Tag this request; only the latest one is allowed to apply its results.
    const seq = ++searchSeq.current;

    // Filter out empty sub-arrays to avoid sending invalid facet filters
    const cleanFacetQuery = facetQuery.filter((group) => group.length > 0);

    axios
      .post(global.postUrl + "restproxy", {
        headers: {
          "X-Algolia-Application-Id": window.rc.algoliasearch.appId,
          "X-Algolia-API-Key": window.rc.algoliasearch.apiKey,
        },
        body: {
          query: searchQuery,
          hitsPerPage: hitsPerPage,
          facets: window.filterOption.facetList,
          facetFilters: cleanFacetQuery,
          numericFilters: rangeQuery,
          page: selectedPage,
        },
        url:
          "https://" +
          window.rc.algoliasearch.appId +
          "-dsn.algolia.net/1/indexes/" +
          (!isEmptySort ? aToZSort.value : selectedSorting) +
          "/query",
        force: false,
      })
      .then((response) => {
        // Ignore responses from superseded requests (e.g. a stale page-N
        // search that resolves after the page was reset to 0 on a sort change).
        if (seq !== searchSeq.current) return;
        console.log(response.data);
        let {
          hits,
          nbHits,
          facets,
          nbPages,
          facets_stats,
        } = response.data.response;

        hits.map((element) => {
          const random = Math.floor(Math.random() * element.variants.length);
          let firstIndex = element.variants[0];
          element.variants[0] = element.variants[random];
          element.variants.push(firstIndex);
          return element;
        });

        // Mobile: Accumulate results for infinite scroll
        // Desktop: Replace results for pagination
        if (isMobile && selectedPage > 0) {
          // On mobile, when loading a page from URL or scrolling,
          // we need to load all pages up to current page
          setSearchResults((prevResults) => {
            // If we're on page 2+, append new results
            const existingIds = new Set(prevResults.map((p) => p.objectID));
            const newHits = hits.filter((h) => !existingIds.has(h.objectID));
            return [...prevResults, ...newHits];
          });
        } else {
          // Desktop or first page on mobile: Replace results
          setSearchResults(hits);
        }

        setTotalProducts(nbHits);
        setFacetFetchValues(facets);
        setTotalPages(nbPages);
        if (Object.keys(facetStatsValues).length == 0) {
          if (rangeQuery.length > 0) {
            // First search had a price filter — stats reflect filtered data.
            // Fetch unfiltered stats so the slider shows the full price range.
            fetchFullPriceStats();
          } else {
            setFacetStatsValues(facets_stats);
          }
        }

        searchFinish();
      })
      .catch((err) => {
        if (seq !== searchSeq.current) return;
        searchFinish();
      });
  };

  const searchFinish = () => {
    setIsFetching(false);
    setShimmer(false);
    setProductShimmer(false);
    setLoadItems(false);
    restoredFromCache.current = false;
    loadingNextPage.current = false;
  };

  // Save listing data to sessionStorage for scroll restoration on back navigation
  useEffect(() => {
    if (searchResults.length > 0 && !shimmer) {
      try {
        sessionStorage.setItem(
          "listing_cache",
          JSON.stringify({
            pathname: window.location.pathname,
            searchResults,
            totalProducts,
            totalPages,
            facetValues,
            facetStatsValues,
          }),
        );
      } catch {}
    }
  }, [
    searchResults,
    totalProducts,
    totalPages,
    facetValues,
    facetStatsValues,
    shimmer,
  ]);

  useEffect(() => {
    if (Object.keys(facetValues).length == 0) setFacetValues(facetFetchValues);
  }, [facetFetchValues]);

  // A new search query (or category) changes which facets are relevant, so the
  // frozen facetValues must be cleared to let the next response repopulate the
  // accordion list. Applying facet/range filters does NOT change searchQuery, so
  // the accordion list still stays stable while filtering — the guard above only
  // writes facetValues while it is empty.
  const skipFacetResetOnMount = useRef(true);
  useEffect(() => {
    // Ignore the synthetic first render; searchQuery starts "" and is set to its
    // real value by the URL effect on mount, which is not a user-driven search.
    if (skipFacetResetOnMount.current) {
      skipFacetResetOnMount.current = false;
      return;
    }
    // On back navigation the sidebar is restored from cache; the mount's
    // URL-driven query change must not wipe it. It stays true until the silent
    // background refresh finishes (searchFinish), by which point the query is
    // settled, so real searches afterwards still reset correctly.
    if (restoredFromCache.current) return;
    setFacetValues({});
  }, [searchQuery]);

  // Handle facet filter changes: rebuild facetQuery and update URL
  useEffect(() => {
    if (isLoadingFromUrl.current) return;

    // Apply filters to search — only include keys with actual values
    const activeFilters = Object.entries(selectedFilters).filter(
      ([, values]) => values && values.length > 0,
    );
    if (activeFilters.length > 0) {
      const appliedFilters = activeFilters.map(([key, values]) =>
        values.map((v) => `${key}:${v}`),
      );
      setFacetQuery((prev) => {
        const prevStr = JSON.stringify(prev);
        const nextStr = JSON.stringify(appliedFilters);
        return prevStr === nextStr ? prev : appliedFilters;
      });
    } else {
      setFacetQuery((prev) => (prev.length === 0 ? prev : []));
    }
  }, [selectedFilters]);

  // Handle range filter changes: set rangeQuery directly
  useEffect(() => {
    if (isLoadingFromUrl.current) return;

    if (selectedRangeFilters && selectedRangeFilters.value) {
      const newRange = `variants.offerprice:${selectedRangeFilters.value.min} TO ${selectedRangeFilters.value.max}`;
      setRangeQuery((prev) =>
        prev.length === 1 && prev[0] === newRange ? prev : [newRange],
      );
    } else {
      setRangeQuery((prev) => (prev.length === 0 ? prev : []));
    }
  }, [selectedRangeFilters]);

  // Sync filters to URL (does NOT trigger search)
  useEffect(() => {
    if (isLoadingFromUrl.current) return;

    const queryParams = new URLSearchParams();

    // Add current filters
    Object.keys(selectedFilters).forEach((facetName) => {
      if (selectedFilters[facetName] && selectedFilters[facetName].length > 0) {
        queryParams.set(facetName, selectedFilters[facetName].join(","));
      }
    });

    // Add range filter if present
    if (selectedRangeFilters && selectedRangeFilters.value) {
      queryParams.set(
        "range",
        `${selectedRangeFilters.value.min}TO${selectedRangeFilters.value.max}`,
      );
    }

    const newUrl = `${window.location.pathname}?${queryParams.toString()}`;
    if (window.location.search !== `?${queryParams.toString()}`) {
      props.history.replace(newUrl);
    }
  }, [selectedFilters, selectedRangeFilters]);

  // Single source of truth for triggering searches.
  // - A bare page change searches directly.
  // - A filter/sort/search change resets to page 0 FIRST; when on a page > 0
  //   that reset re-triggers this effect, so the search runs exactly once and
  //   always with the correct (reset) page — never the stale last-page value.
  // This ordering is what fixes the "sort on the last page shows wrong page 1"
  // bug: previously a separate search effect fired with the stale page before
  // the reset effect ran, and the corrective search was dropped by isFetching.
  useEffect(() => {
    if (!fetchProducts) return;

    const filterKey = JSON.stringify([
      facetQuery,
      rangeQuery,
      searchQuery,
      selectedSorting,
    ]);
    const filterDepsChanged =
      prevFilterDeps.current !== null && prevFilterDeps.current !== filterKey;
    prevFilterDeps.current = filterKey;

    // User-initiated filter/sort/search change (not an initial URL load).
    if (filterDepsChanged && !isLoadingFromUrl.current) {
      if (selectedPage !== 0) {
        // Reset to page 0; the resulting selectedPage change re-triggers this
        // effect, which then runs the actual search at page 0. Skip searching
        // with the stale page here.
        resetPagination();
        return;
      }
      // Already on page 0: no re-trigger will occur, so do the URL/scroll
      // housekeeping now and fall through to search at page 0.
      resetPagination();
    }

    // Don't replace product grid with skeleton on mobile infinite scroll —
    // the bottom spinner (isFetching) handles loading indication for next pages
    if (
      !loadItems &&
      !restoredFromCache.current &&
      !(isMobile && selectedPage > 0)
    ) {
      setProductShimmer(true);
    }
    search();
  }, [
    facetQuery,
    rangeQuery,
    selectedPage,
    searchQuery,
    selectedSorting,
    fetchProducts,
  ]);

  const resetPagination = () => {
    // Reset page state directly so the next search uses page 0
    setSelectedPage(0);
    // Also update URL to remove page param
    const queryParams = new URLSearchParams(window.location.search);
    queryParams.delete("page");
    const newUrl = `${window.location.pathname}?${queryParams.toString()}`;
    if (window.location.search !== `?${queryParams.toString()}`) {
      props.history.replace(newUrl);
    }
    // Scroll to top when filters/search/sorting change (not during pagination)
    const element = document.getElementsByClassName("site-main");
    if (element.length > 0) {
      element[0].scrollTop = 0;
    }
  };

  const resetFilters = () => {
    setShimmer(true);
    setProductShimmer(true);
    setSearchResults([]);
    setFacetQuery([]);
    setRangeQuery([]);
    setSelectedFilters({});
    setSelectedRangeFilters({});
    setFacetStatsValues({});
    setLoadRange(false);
  };

  return {
    hitsPerPage,
    categorySlug,
    category,
    categoryError,
    shimmer,
    productShimmer,
    showModal,
    setShowModal,
    loadRange,
    setLoadRange,
    searchQuery,
    searchResults,
    totalProducts,
    totalPages,
    selectedPage,
    selectedFilters,
    setSelectedFilters,
    selectedRangeFilters,
    setSelectedRangeFilters,
    selectedSorting,
    setSelectedSorting,
    isMobile,
    isFetching,
    facetValues,
    facetStatsValues,
    rangeQuery,
    handlePageChange,
    loadNextPage,
    resetFilters,
  };
};

export default useListing;
